import i18next from 'i18next';
import BaseTableOptionsDropdown from 'components/atoms/base-table-option-dropdown';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { convertSecondsToTime } from 'utils/timeHelper';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';

import s from './styles.module.scss';

import type { SortState } from 'infrastructure/types';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type { IAllergyLog, IUpdateAllergyLog } from 'infrastructure/interfaces';

interface Props {
  setLogForUpdate: (args: IUpdateAllergyLog) => void;
  loading: boolean;
  disableActions: boolean;
  updatedItemGuid?: string;
  documentationTimeSort: SortState;
  updatedAtSort: SortState;
  epipenRequiredSort: SortState;
}

export const columns = ({
  setLogForUpdate,
  loading,
  updatedItemGuid,
  disableActions,
  documentationTimeSort,
  updatedAtSort,
  epipenRequiredSort,
}: Props): IBaseTableColumns<IAllergyLog> => {
  return [
    {
      label: i18next.t('labels.dateTime'),
      key: 'updatedAt',
      width: '20%',
      render(date: string) {
        return (
          <span data-cy="allergy-log-updatedAt">
            {DateFormatter({
              date,
              format: DateFormats['MM/dd/yyyy, hh:mm:ss a'],
            })}
          </span>
        );
      },
      align: 'left',
      sortable: true,
      sortKey: 'updatedAt',
      sortOrder: tableSortMap[updatedAtSort],
    },
    {
      label: i18next.t('labels.documentTime'),
      key: 'documentationTime',
      align: 'left',
      width: '15%',
      render(documentationTime?: number | null) {
        const actionTime = !documentationTime
          ? DOCUMENTATION_TIME_PLACEHOLDER
          : convertSecondsToTime(documentationTime);
        return <span data-cy="voice-documentation-time">{actionTime}</span>;
      },
      sortable: true,
      sortKey: 'documentationTime',
      sortOrder: tableSortMap[documentationTimeSort],
    },
    {
      label: i18next.t('labels.allergy'),
      key: 'allergy',
      width: '25%',
      render(value: string) {
        return <span data-cy="allergy-log-allergy">{value}</span>;
      },
    },
    {
      label: i18next.t('labels.allergicResponse'),
      key: 'allergicResponse',
      width: '25%',
      render(value: string) {
        return (
          <span
            className={s['allergic-response']}
            data-cy="allergy-log-allergicResponse"
          >
            {value}
          </span>
        );
      },
    },
    {
      label: i18next.t('labels.epipenRequired'),
      key: 'epipenRequired',
      width: '15%',
      render(value: boolean) {
        return (
          <span data-cy="allergy-log-epipenRequired">
            {value ? i18next.t('controls.yes') : i18next.t('controls.no')}
          </span>
        );
      },
      align: 'left',
      sortable: true,
      sortKey: 'epipenRequired',
      sortOrder: tableSortMap[epipenRequiredSort],
    },
    {
      label: '',
      key: 'guid',
      width: '5%',
      render(guid, item) {
        return (
          <BaseTableOptionsDropdown
            loading={loading && guid === updatedItemGuid}
            onEdit={() =>
              setLogForUpdate({
                allergicResponse: item.allergicResponse,
                epipenRequired: item.epipenRequired,
                allergy: item.allergy,
                guid: item.guid,
                patientGuid: item.patientGuid,
              })
            }
            disabled={disableActions}
          />
        );
      },
    },
  ];
};
